import  {create}  from 'zustand'
import type { ReactNode } from 'react'
import { produce } from 'immer'
import {materialDefaults,deepClone, material50cm} from './libs/util'
import { cloneWithOffset, DUPLICATE_OFFSET } from './libs/duplicateObjects'
import { trackGroupAction, trackMultiSelectMode, ModeSource } from './libs/multiSelectAnalytics'
import { InspectorMode, PhysicalObjectInterface, SideBarMode  } from './ts/app_interfaces';
// NEVER import three or @react-three/fiber here — MattoState is in the boot
// path and a static import drags the whole three ecosystem into the entry chunk.

// One dataTransfer entry of a library drag, captured as a string so the armed
// state stays plain data (see buildDragPayload in libs/product_helpers).
export interface DragPayload { key: string; json: string }

// Everything needed to redraw the tile the browser would have snapshotted as
// the drag image, so click-to-place and drag look identical: the tile's own
// loaded image, its rendered box, and where inside it the pointer grabbed.
export interface ArmedGhost {
  src: string | null
  width: number
  height: number
  radius: string
  fit: string
  grabX: number
  grabY: number
  startX: number
  startY: number
}

export interface ArmedPlacement {
  payloads: DragPayload[]
  product: { id: string; name: string; thumbnail: string | null; productType: string }
  ghost?: ArmedGhost | null
  // What the tile holds, for the status bar's wording ("click the image again").
  kind?: ArmedKind
  // The sidebar it was armed from (analytics).
  source: ArmedSource
}

export type ArmedKind = 'material' | 'paint' | 'image' | 'object'
export type ArmedSource = 'library' | 'my-library' | 'images' | 'collections' | 'tray' | 'studio-library'

export const defaultCameraPosition = [1,0,0,0,0,0.0000010001554604510685,-0.9999999999994995,0,0,0.9999999999994995,0.0000010001554604510685,0,0,49.367843386243472,0.000015370232479014257,1]

// Canonical "board content" signature used to derive isStateDirty. Includes only
// the fields a user considers an edit AND that get persisted by buildProject():
// objects, title, lighting, background, envmap. Deliberately excludes camera /
// controlsTarget / projectId so orbiting the camera or selecting an object does
// NOT mark the board dirty.
export const computeBoardSignature = (physicalObjects:any, projectData:any): string => {
  return JSON.stringify({
    physicalObjects: physicalObjects ?? [],
    title: projectData?.title ?? null,
    lighting: projectData?.lighting ?? null,
    backgroundTexture: projectData?.backgroundTexture ?? null,
    envmap: projectData?.envmap ?? null,
  })
}

// Pure dirty predicate shared by the SaveButton tracker and the store actions.
// null baseline = never-saved board → dirty once it has any content; otherwise
// dirty when the current signature diverges from the saved baseline.
export const isBoardDirty = (savedSignature: string | null, physicalObjects:any, projectData:any): boolean => {
  if (savedSignature === null) return (physicalObjects?.length ?? 0) > 0
  return computeBoardSignature(physicalObjects, projectData) !== savedSignature
}

// Pure predicate for the autosave tick: only autosave when the board has
// unsaved changes, no save is already in flight, a user is logged in, AND the
// user is not mid-interaction. The interaction guard is measured at the input
// layer (pointer up/down + keydown) rather than from store changes, because
// object drag/rotate/scale only commit to the store on release and the gizmo
// path never sets selectedObjectIsChanging — so an input-idle gap is the only
// signal that reliably covers every manipulation. A clean/idle board, a
// logged-out one, or one being actively manipulated is a no-op.
export const shouldAutosave = (
  { isStateDirty, saveProjectNow }: { isStateDirty: boolean; saveProjectNow: boolean },
  hasUser: boolean,
  activity: { isPointerDown?: boolean; msSinceLastInput?: number; quietPeriodMs?: number } = {},
): boolean => {
  const { isPointerDown = false, msSinceLastInput = Infinity, quietPeriodMs = 700 } = activity
  return Boolean(
    isStateDirty && !saveProjectNow && hasUser && !isPointerDown && msSinceLastInput >= quietPeriodMs,
  )
}


export const useMattoState = create( (set:any, get:any) => ({
  post: { "UseMetalRougness":true, "BakeShadowsOnDrag":true,"InRealTime":true, "Ambient":true, "DepthOfField":false, "Bloom":false, "AdaptievPixelRatio":true, "bias": -0.00089, "normalBias":0,"radius":4.5, "samples":10},
  showGrid:false,
  inspectorMode:InspectorMode.Materials,
  sideBarMode:SideBarMode.Library,
  uploadsTabIndex:1, // 0 for CURATED, 1 for MY IMAGES
  templatesTabIndex:0, // 0 for CURATED, 1 for BLANK
  collectionsTabIndex:1, // 0 for CURATED, 1 for MY COLLECTIONS
  modal:null,
  profile:{},
  controls:null,
  transformMode:null,
  // Click-to-place: a library material "checked out" by a tile click, waiting
  // for a tap on the board canvas (ArmedPlacement.tsx). Payloads are the same
  // dataTransfer key/JSON pairs a drag would carry, so the tap can dispatch a
  // synthetic drop and inherit DropPhysicalObject unchanged. Plain JSON only.
  armedPlacement: null as ArmedPlacement | null,
  setArmedPlacement: (armedPlacement: ArmedPlacement | null) => set({ armedPlacement }),
  clearArmedPlacement: () => set({ armedPlacement: null }),
  // The toolbar's Lighting panel is open (LightingButton); the board status
  // bar swaps to the light-position hint while it is.
  lightingPanelOpen: false,
  setLightingPanelOpen: (lightingPanelOpen: boolean) => set({ lightingPanelOpen }),
  selectedObjectIsChanging:false,
  // Board multi-select mode (ActionBar toggle / `m`): every object click toggles
  // membership and a drag on empty canvas box-selects. Touch has no modifier
  // keys, so this is the iPad path.
  multiSelectMode:false,
  // Group rotate/scale pivot: 'group' turns the arrangement as one body about
  // the shared centre; 'each' turns/scales every member about its own base.
  // Session-sticky (not reset on selection change).
  groupPivotMode:'group' as 'group' | 'each',
  isUploading:false,
  saveProjectNow: false,
  denyUpload:false,
  isStateDirty:false,
  // Baseline content signature of the board's last saved/loaded clean state.
  // null = board has never been saved (new / template / collection / generated):
  // in that case dirty is derived purely from "has content". Otherwise dirty is
  // derived by diffing the current signature against this baseline.
  savedSignature: null as string | null,
  userFavoritesData:{},
  favoriteSelected:false,
  copiedMaterialData:{},
  doAnimate:false,
  // While recording, RecordingCapture mirrors gizmo-free frames into this canvas/stream
  recordingTarget: null as { canvas: HTMLCanvasElement; stream: MediaStream } | null,
  snackbarMessage:null,
  snackbarAutoHide:true,
  // Optional node rendered in the snackbar's action slot (e.g. an "UNDO" button
  // after an archive). Cleared implicitly on every setSnackbarMessage call, so
  // a stale action can never outlive its message.
  snackbarAction: null as ReactNode,
  gpuData:null,
  userCollectionData:[],
  paintSearchCode:null,
  showSubscription :null,
  backgroundRemovalProcessing: false,
  pdfProcessing: false,
  useBetaVersion: true,

  // Active concept state - activeConceptImageUrl is set by useEditorGuard, read by Bar for navigation
  activeConceptId: null as string | null,
  activeConceptImageUrl: null as string | null,
  setActiveConceptId: (activeConceptId: string | null) => set({ activeConceptId }),
  setActiveConceptImageUrl: (activeConceptImageUrl: string | null) => set({ activeConceptImageUrl }),

  // Parent project of the schedule currently open in the editor (#3621). Set by
  // ScheduleEditorPage once the doc loads, read by HomeSideBar so the schedule
  // editor keeps the project rail — a schedule is always opened from a project.
  // Lives here rather than being read off scheduleEditorStore so the sidebar
  // doesn't pull the lazily-split schedules chunk into the Home bundle.
  activeScheduleProjectId: null as string | null,
  setActiveScheduleProjectId: (activeScheduleProjectId: string | null) =>
    set({ activeScheduleProjectId }),

  // Unified busy lock - true when any generation/edit is in progress
  conceptBusy: false,
  setConceptBusy: (conceptBusy: boolean) => set({ conceptBusy }),

  // Board AutoComposeFromCollection is placing objects onto (incl. the staggered
  // drop window). LoadScene skips its reset only for THIS id — a boolean would
  // let a stale flag make an unrelated board skip its own load.
  autoComposeBoardId: null as string | null,
  setAutoComposeBoardId: (autoComposeBoardId: string | null) => set({ autoComposeBoardId }),

  // SideBar panel visibility (used in detail mode on concepts2 routes)
  sideBarPanelOpen: true,
  setSideBarPanelOpen: (sideBarPanelOpen: boolean) => set({ sideBarPanelOpen }),

  // Initial generation streaming state — bridges ConceptScheme → ConceptPage
  initialStreamConceptId: null as string | null,
  initialStreamText: "",
  initialStreamStatus: null as string | null,
  initialStreamPayloads: [] as any[],
  initialStreamStartedAt: null as number | null,
  initialStreamInterimDescription: null as { content: string; keywords: string[] } | null,
  startInitialStream: (conceptId: string) => set({
    initialStreamConceptId: conceptId,
    initialStreamText: "",
    initialStreamStatus: null,
    initialStreamPayloads: [],
    initialStreamStartedAt: Date.now(),
    initialStreamInterimDescription: null,
  }),
  appendInitialStreamText: (token: string) => set((state: any) => ({
    initialStreamText: state.initialStreamText + token,
  })),
  setInitialStreamStatus: (status: string | null) => set({ initialStreamStatus: status }),
  appendInitialStreamPayload: (payload: any) => set((state: any) => ({
    initialStreamPayloads: [...state.initialStreamPayloads, payload],
  })),
  setInitialStreamInterimDescription: (desc: { content: string; keywords: string[] } | null) => set({ initialStreamInterimDescription: desc }),
  clearInitialStream: () => set({
    initialStreamConceptId: null,
    initialStreamText: "",
    initialStreamStatus: null,
    initialStreamPayloads: [],
    initialStreamStartedAt: null,
    initialStreamInterimDescription: null,
  }),

  // Image Editor Apply Mode - when a segment is selected and waiting for material click
  imageEditorApplyMode: null as {
    active: boolean;
    targetSegmentLabel: string;
    onMaterialSelect: ((material: any) => void);
  } | null,
  setImageEditorApplyMode: (mode: { active: boolean; targetSegmentLabel: string; onMaterialSelect: (material: any) => void } | null) => set({ imageEditorApplyMode: mode }),
  clearImageEditorApplyMode: () => set({ imageEditorApplyMode: null }),
  
  setSnackbarMessage: (snackbarMessage, snackbarAutoHide=true, snackbarAction=null)=> set({snackbarMessage, snackbarAutoHide, snackbarAction}),
  setBackgroundRemovalProcessing: (backgroundRemovalProcessing)=> set({backgroundRemovalProcessing}), 
  setPdfProcessing: (pdfProcessing)=> set({pdfProcessing}),
  setModal: (modal)=> set({modal}),
  setDoAnimate: (doAnimate)=> set({doAnimate}),
  setRecordingTarget: (recordingTarget: { canvas: HTMLCanvasElement; stream: MediaStream } | null) => set({recordingTarget}),
  setFavoriteSelected: (favoriteSelected)=>set({favoriteSelected}),
  setPaintSearchCode: (paintSearchCode)=>set({paintSearchCode}),
  setCopiedMaterialData:(copiedMaterialData)=>set({copiedMaterialData}),
  setInspectorMode: (inspectorMode) => set({inspectorMode}),
  setSideBarMode: (sideBarMode) => set({sideBarMode}),
  setUploadsTabIndex: (uploadsTabIndex) => set({uploadsTabIndex}),
  setTemplatesTabIndex: (templatesTabIndex) => set({templatesTabIndex}),
  setCollectionsTabIndex: (collectionsTabIndex) => set({collectionsTabIndex}),
  setControls: (controls) => set({controls}),
  setTransformMode: (transformMode) => set({transformMode}),
  setSelectedObjectIsChanging: (selectedObjectIsChanging) => set({selectedObjectIsChanging}),
  setMultiSelectMode: (multiSelectMode: boolean, source: ModeSource = 'api') => {
    if (get().multiSelectMode === multiSelectMode) return;
    set({multiSelectMode})
    trackMultiSelectMode(multiSelectMode, source)
  },
  setGroupPivotMode: (groupPivotMode: 'group' | 'each') => set({groupPivotMode}),
  toggleGroupPivotMode: () => set((state) => ({ groupPivotMode: state.groupPivotMode === 'each' ? 'group' : 'each' })),
  toggleMultiSelectMode: (source: ModeSource = 'api') => get().setMultiSelectMode(!get().multiSelectMode, source),
  toggleGrid: () => set(state => ({ showGrid: !state.showGrid })),
  setProfile: (profile)=> set({profile}),
  setStateDirty: (isStateDirty)=> set({isStateDirty}),
  // Snapshot the current board as the clean baseline (used after a save, and
  // after loading the user's own saved board). Clears isStateDirty.
  markBoardClean: () => set({
    savedSignature: computeBoardSignature(usePhysicalObjects.getState().physicalObjects, useProjectData.getState()),
    isStateDirty: false,
  }),
  // Mark the board as an unsaved/never-saved board (new, template, collection,
  // shared copy, generated). Dirty becomes true as soon as there is any content.
  markBoardUnsaved: () => set({
    savedSignature: null,
    isStateDirty: isBoardDirty(null, usePhysicalObjects.getState().physicalObjects, useProjectData.getState()),
  }),
  setUserFavoritesData: (userFavoritesData)=> set({userFavoritesData}),
  setUserCollectionData: (userCollectionData)=> set({userCollectionData}),
  setGpuData: (gpuData)=> set({gpuData}),
  setIsRendering: (isRendering)=> set({isRendering}),
  setShowSubscription: (showSubscription)=> set({showSubscription}),
  setUseBetaVersion: (useBetaVersion)=> set({useBetaVersion}),
  set: fn => set(produce(fn)),
}))

export const projectDataDefaults = {
  projectId:null, userId:null,scene:'original',title:'Untitled',envmap:'studio',cameraMatrix:null,
  lighting:{ambient:0.25, directional:62, colorTemperature:7500,directionalIntensity:2.5,radius:3,samples:12, exposure:1.0},
  backgroundTexture:null, cameraPosition:null, controlsTarget:null, associatedProjectId:null, conceptId:null
}
export const useProjectData = create( (set:any) => ({...projectDataDefaults, 
  ...{update: (d) => set(d)},
  ...{reset: () => set(projectDataDefaults)},
  ...{set: fn => set(produce(fn)),}}))
// //projectData.set( d => { d.lighting.ambient=1 } )

// ---------------------------------------------------------------------------
// Selection model
//
// `selectedKeys` is the source of truth (insertion-ordered; the LAST key is the
// "primary" — the object the inspector pane, gizmo and floating toolbar act
// on). `selectedPhysicalObjectKey` / `selectedPhysicalObject` are kept as
// derived fields so the many single-select consumers keep working unchanged:
// with 2+ selected they hold the primary, never null (null means "edit the
// floor background" in the inspector).
//
// Keys are Math.random() numbers in the store but String(key) in three.js
// userData, so every comparison here is loose (==) on purpose.
// ---------------------------------------------------------------------------
const sameKey = (a: any, b: any) => a == b
const keyIn = (keys: any[], key: any) => keys.some((k) => sameKey(k, key))
const sameKeyList = (a: any[], b: any[]) => a.length === b.length && a.every((k, i) => sameKey(k, b[i]))
// The current selection. Tolerates state written through the legacy fields
// alone (tests and debug helpers still do `setState({selectedPhysicalObjectKey})`).
const selectedKeysOf = (state: any): any[] => {
  if (state.selectedKeys?.length) return state.selectedKeys
  return state.selectedPhysicalObjectKey != null ? [state.selectedPhysicalObjectKey] : []
}

// Single choke point for every selection write (must be called inside an immer
// draft). Dedupes, drops keys that are not on the board, and refreshes the
// derived primary fields. Leaves `selectedKeys` reference-stable when the
// resulting list is unchanged so subscribers don't re-render on every commit.
const applySelection = (draft: any, keys: any[]) => {
  const next: any[] = []
  keys.forEach((k) => {
    if (k == null || keyIn(next, k)) return
    if (draft.physicalObjects.some((p: any) => sameKey(p.key, k))) next.push(k)
  })
  if (!sameKeyList(draft.selectedKeys ?? [], next)) draft.selectedKeys = next
  const primary = next.length ? next[next.length - 1] : null
  draft.selectedPhysicalObjectKey = primary
  draft.selectedPhysicalObject = primary == null
    ? null
    : (draft.physicalObjects.find((p: any) => sameKey(p.key, primary)) ?? null)
}

// Objects dropped from the library arrive without materialData; fill in the
// defaults (mutates in place, as addPhysicalObject always has).
const ensureMaterialData = (physicalObject: any, draggedIn: boolean) => {
  if (physicalObject.materialData) return
  if (physicalObject.isStaticObject) {
    //no materialProps necessary
    physicalObject.materialData = {materialProps: {}}
  }
  else {
    const materialProps = deepClone(materialDefaults)
    if (draggedIn==true) materialProps.color = 0xC5C5C5;
    physicalObject.materialData = {materialProps: materialProps}
  }
}

const readTransform = (threeJSObject: any) => ({
  position: [threeJSObject.position.x, threeJSObject.position.y, threeJSObject.position.z],
  scale: [threeJSObject.scale.x, threeJSObject.scale.y, threeJSObject.scale.z],
  rotation: [threeJSObject.rotation.x, threeJSObject.rotation.y, threeJSObject.rotation.z],
})

const findSceneObject = (scene: any, key: any) => {
  if (!scene?.children) return null
  return scene.children.find((child: any) => {
    if (!child.userData) child.userData = {} // Ensure userData exists
    return sameKey(child.userData.key, key)
  }) ?? null
}

export const usePhysicalObjects = create( ( set:any,get:any) => ({
  physicalObjects:[],
  draggedObject:[], //object keys from objects they were dragged in (as opposed to from saved projects)
  selectedKeys:[] as any[],
  selectedPhysicalObject:null,
  selectedPhysicalObjectKey: null,
  updateCount:0,
  lockedObjects:[],

  selectedMaterialndex:0,
  setSelectedMaterialndex: (selectedMaterialndex)=> set({selectedMaterialndex}),

  // ---- selection ----------------------------------------------------------
  setSelection: (keys: any[]) => {
    set(produce((draft: any) => { applySelection(draft, keys ?? []) }))
  },
  // Union; each given key moves to the end so the last one becomes primary.
  addToSelection: (keys: any[]) => {
    set(produce((draft: any) => {
      applySelection(draft, [...selectedKeysOf(draft).filter((k: any) => !keyIn(keys, k)), ...keys])
    }))
  },
  toggleSelection: (key: any) => {
    set(produce((draft: any) => {
      const current = selectedKeysOf(draft)
      const next = keyIn(current, key)
        ? current.filter((k: any) => !sameKey(k, key))
        : [...current, key]
      applySelection(draft, next)
    }))
  },
  clearSelection: () => {
    set(produce((draft: any) => { applySelection(draft, []) }))
  },
  // Locked objects can't be moved, so a "select all" that included them would
  // make every group action silently skip members. Opt in with includeLocked.
  selectAll: ({ includeLocked = false }: { includeLocked?: boolean } = {}) => {
    set(produce((draft: any) => {
      applySelection(draft, draft.physicalObjects
        .filter((p: any) => includeLocked || !keyIn(draft.lockedObjects, p.key))
        .map((p: any) => p.key))
    }))
  },
  isSelected: (key: any) => keyIn(selectedKeysOf(get()), key),
  getSelectedKeys: () => selectedKeysOf(get()),
  // Legacy single-select setter: null clears, a key replaces the selection.
  setSelectedPhysicalObjectKey: (selectedPhysicalObjectKey) =>  {
    if (selectedPhysicalObjectKey == null) get().clearSelection()
    else get().setSelection([selectedPhysicalObjectKey])
  },
  getUpdateCount: () => { return get().updateCount },
  getCurrentSelectedPhysicalObject: () => {
    const k = get().selectedPhysicalObjectKey
    return k==null ? null : get().getSelectedPhysicalObject(k)
  },
  getSelectedPhysicalObject: (key) => {
    const physicalObjects = get().physicalObjects
    const index = physicalObjects.findIndex( (p) => p.key== key)
    return physicalObjects[index]
  },
  // Bulk replace (board load, undo/redo). Clears dragged/locked state. The
  // selection is dropped unless `preserveSelection`, in which case it is kept
  // and pruned to objects that still exist (undo of a paste removes the
  // clones that were selected). Plain `set`: loaded arrays must not be
  // deep-frozen by immer.
  setPhysicalObjects: (physicalObjects, { preserveSelection = false }: { preserveSelection?: boolean } = {}) => {
    const keep = preserveSelection
      ? selectedKeysOf(get()).filter((k) => physicalObjects.some((p) => sameKey(p.key, k)))
      : []
    const primary = keep.length ? keep[keep.length - 1] : null
    set({
      physicalObjects,
      draggedObject: [],
      lockedObjects: [],
      selectedKeys: keep,
      selectedPhysicalObjectKey: primary,
      selectedPhysicalObject: primary == null ? null : (physicalObjects.find((p) => sameKey(p.key, primary)) ?? null),
    })
  },
  isObjectDraggedIn: (physicalObject) => {
    const draggedObject = get().draggedObject
    const index = draggedObject.findIndex( (objectKey) => objectKey == physicalObject.key)
    return index!=-1
  },

  // ---- lock ---------------------------------------------------------------
  isObjectLocked: (selectedPhysicalObjectKey) => {
    const lockedObjects = get().lockedObjects
    const index = lockedObjects.findIndex( (objectKey) => objectKey == selectedPhysicalObjectKey)
    return index!=-1
  },
  lockObjects: (keys: any[]) => {
    set( (state) => ({
      lockedObjects: [...state.lockedObjects, ...keys.filter((k) => !keyIn(state.lockedObjects, k))],
    }))
  },
  unlockObjects: (keys: any[]) => {
    set( (state) => ({
      lockedObjects: state.lockedObjects.filter((objectKey) => !keyIn(keys, objectKey)),
    }))
  },
  // True only when something is selected and every selected object is locked.
  // The lock button / Cmd+Shift+L use the "any unlocked → lock all" rule.
  isSelectionLocked: () => {
    const selectedKeys = selectedKeysOf(get())
    const { lockedObjects } = get()
    return selectedKeys.length > 0 && selectedKeys.every((k) => keyIn(lockedObjects, k))
  },
  toggleSelectionLock: () => {
    const keys = selectedKeysOf(get())
    const unlock = get().isSelectionLocked()
    if (unlock) get().unlockObjects(keys)
    else get().lockObjects(keys)
    trackGroupAction(unlock ? 'unlock' : 'lock', keys.length)
  },
  lockSelectedObject: () => {
    const keys = selectedKeysOf(get())
    if (keys.length) get().lockObjects(keys)
  },
  unLockSelectedObject: () => {
    const keys = selectedKeysOf(get())
    if (keys.length) get().unlockObjects(keys)
  },

  // ---- type predicates (primary) -----------------------------------------
  isSelectedDynamicMaterial: () => {
    const selectedPhysicalObject = get().selectedPhysicalObject
    if (selectedPhysicalObject==null) return false;
    if (selectedPhysicalObject.type == 'dynamic' && selectedPhysicalObject.mode=='material') return true
    else { return false; }
  },

  isSelectedObjectStatic: (scene=window.scene) => {
    const selectedPhysicalObject = get().selectedPhysicalObject
    if (selectedPhysicalObject==null) return false;
    if (selectedPhysicalObject.isStaticObject != undefined) return selectedPhysicalObject.isStaticObject
    else {
      const threeJSObj = scene.children.find( (child) => child.userData.key == selectedPhysicalObject.key)
      return !(threeJSObj?.children?.length===1);
    }
  },
  isSelectedObjectDynamic: () => {
    const selectedPhysicalObject = get().selectedPhysicalObject
    if (selectedPhysicalObject==null) return false;
    if (selectedPhysicalObject.type == 'dynamic') return true
    else {
      return false;
    }
  },
  isSelectedObjectPaint: () => {
    const selectedPhysicalObject = get().selectedPhysicalObject
    if (selectedPhysicalObject==null) return false;
    if (selectedPhysicalObject.type == 'paint') return true
    else {
      return false;
    }
  },

  // ---- add / update / delete ---------------------------------------------
  // Adds and selects the given objects (last one is primary). One store write.
  addPhysicalObjects: (physicalObjects: (PhysicalObjectInterface | any)[], draggedIn=false) => {
    if (!physicalObjects?.length) return;
    physicalObjects.forEach((p) => ensureMaterialData(p, draggedIn))
    set(produce( (draft:any) => {
      physicalObjects.forEach((p) => {
        draft.physicalObjects.push(p)
        draft.draggedObject.push( draggedIn ? p.key : null)
      })
      applySelection(draft, physicalObjects.map((p) => p.key))
    }))
  },
  addPhysicalObject:(physicalObject:PhysicalObjectInterface | any, draggedIn=false) => {
    get().addPhysicalObjects([physicalObject], draggedIn)
  },
  // Clone + offset the given objects (same offset for all, so a set keeps its
  // relative layout) and select the clones. Returns the new keys.
  duplicateObjects: (keys: any[], offset: number[] = DUPLICATE_OFFSET) => {
    const clones = keys
      .map((k) => get().getSelectedPhysicalObject(k))
      .filter(Boolean)
      .map((p) => cloneWithOffset(p, offset))
    if (!clones.length) return []
    get().addPhysicalObjects(clones, true)
    trackGroupAction('duplicate', clones.length)
    return clones.map((c) => c.key)
  },
  // Replace objects by key in ONE store write. Never changes which keys are
  // selected; only refreshes the derived primary snapshot.
  updatePhysicalObjects: (physicalObjects: any[]) => {
    set (produce( (draft:any) => {
      physicalObjects.forEach((p) => {
        const index = draft.physicalObjects.findIndex( (q) => sameKey(q.key, p.key))
        if (index >= 0) draft.physicalObjects[index] = p
      })
      applySelection(draft, selectedKeysOf(draft))
      draft.updateCount += 1
    }))
  },
  // Single-object update. If the object is already selected the set is kept
  // (multi-select); otherwise it becomes the selection, as it always has.
  updatePhysicalObject: (physicalObject) => {
    set (produce( (draft:any) => {
      const index = draft.physicalObjects.findIndex( (p) => sameKey(p.key, physicalObject.key))
      if (index < 0) {
        // Legacy tolerance: callers (and tests) have always been able to push
        // an object that isn't in physicalObjects and see it as the selection.
        draft.selectedPhysicalObject = physicalObject
        draft.selectedPhysicalObjectKey = physicalObject.key
        draft.selectedKeys = [physicalObject.key]
        draft.updateCount += 1
        return
      }
      draft.physicalObjects[index] = physicalObject;
      const current = selectedKeysOf(draft)
      applySelection(draft, keyIn(current, physicalObject.key) ? current : [physicalObject.key])
      draft.updateCount += 1
    }))
  },
  deleteObjects: (keys: any[]) => {
    if (!keys?.length) return;
    trackGroupAction('delete', keys.length)
    set(produce( (draft:any) => {
      draft.physicalObjects = draft.physicalObjects.filter((p) => !keyIn(keys, p.key))
      draft.draggedObject = draft.draggedObject.filter((k) => k == null || !keyIn(keys, k))
      draft.lockedObjects = draft.lockedObjects.filter((k) => !keyIn(keys, k))
      applySelection(draft, selectedKeysOf(draft).filter((k) => !keyIn(keys, k)))
    }))
  },
  // Deletes EVERY selected object (Backspace, toolbar delete).
  deleteSelectedObject: () => {
    get().deleteObjects(selectedKeysOf(get()))
  },
  // Deletes one object; only that key leaves the selection.
  deleteObject: (key) => {
    get().deleteObjects([key])
  },

  // ---- transform commits (three.js → store) ------------------------------
  // Reads position/scale/rotation off the live three.js objects for `keys` and
  // writes them all in ONE store write (one render, one undo step). Never
  // changes which keys are selected.
  updateObjectStates: (keys = selectedKeysOf(get()), scene=window.scene) => {
    if (!scene?.children) return;
    const snapshots = keys
      .map((key) => { const o = findSceneObject(scene, key); return o ? { key, ...readTransform(o) } : null })
      .filter(Boolean)
    if (!snapshots.length) return;
    set (produce( (draft:any) => {
      snapshots.forEach((s: any) => {
        const index = draft.physicalObjects.findIndex( (p:any) => sameKey(p.key, s.key))
        if (index < 0) return
        draft.physicalObjects[index].position = s.position
        draft.physicalObjects[index].scale = s.scale
        draft.physicalObjects[index].rotation = s.rotation
      })
      applySelection(draft, selectedKeysOf(draft))
    }))
  },
  // Commit one object's live transform. The object passed in may carry other
  // edits (e.g. originalScale from handleMeshLoad) which are kept. Unlike the
  // pre-multi-select version this NEVER selects the object — a transform commit
  // must not collapse a multi-selection (or steal it while a mesh loads).
  // `updateSelectedObject` is accepted for call-site compatibility only.
  updateObjectState: (physicalObject, scene=window.scene, updateSelectedObject=true) => {
    if (!scene?.children || !physicalObject) return;
    const threeJSObject = findSceneObject(scene, physicalObject.key)
    if (!threeJSObject) return;
    const transform = readTransform(threeJSObject)
    const newPhysicalObject:any = produce(physicalObject,draftState => {
      draftState.position = transform.position
      draftState.scale = transform.scale
      draftState.rotation = transform.rotation
    })
    set (produce( (draft:any) => {
      const index = draft.physicalObjects.findIndex( (p:any) => sameKey(p.key, newPhysicalObject.key))
      if (index < 0) return
      draft.physicalObjects[index] = newPhysicalObject;
      applySelection(draft, selectedKeysOf(draft))
    }))
  },
  updateAllObjectStates: (scene=window.scene) => {
    get().updateObjectStates(get().physicalObjects.map((p) => p.key), scene)
  },
  getThreeJSObject: (key,scene=window.scene) => {
    if (!key || scene==null) return null;
    const threeJSObject = scene.children.filter( (child) => child.userData.key == key)[0]
    return threeJSObject
  },
  getThreeJSObjects: (keys: any[], scene=window.scene) => {
    if (!scene?.children) return [];
    return keys.map((k) => findSceneObject(scene, k)).filter(Boolean)
  },
  getSelectedThreeJSObjects: (scene=window.scene) => {
    return get().getThreeJSObjects(selectedKeysOf(get()), scene)
  },
  //window.scene.children[2].children[0].material.color
  getCurrentThreeJSObject: (scene=window.scene) => {
     const selectedPhysicalObjectKey = get().selectedPhysicalObjectKey
     if (selectedPhysicalObjectKey==null || scene==null) return null;
     const threeJSObject = scene.children.filter( (child) => child.userData.key == selectedPhysicalObjectKey)[0]
     return threeJSObject
  }
}))

if (!window.debug) window.debug = {};
if (!window.debug.invalidate) window.debug.invalidate = () => import('@react-three/fiber').then(({ invalidate }) => invalidate());
window.debug.getPhysicalObjects = () => { return usePhysicalObjects.getState() }
window.debug.getProjectData = () => { return useProjectData.getState()}
window.debug.getMattoState = () => { return useMattoState.getState()}
window.debug.setConceptBusy = (busy: boolean) => useMattoState.getState().setConceptBusy(busy);
window.debug.currentObjectByName= (name) => {
  const objects:any =  usePhysicalObjects.getState()
  objects.setSelectedPhysicalObjectKey(objects.physicalObjects.find( p => p.name==name).key)
}
window.debug.getCurrentObject = () => {
  const objects =  usePhysicalObjects.getState()
  const currentObject = objects.getSelectedPhysicalObject(objects.selectedPhysicalObjectKey)
  return currentObject
}
window.debug.getCurrentThreeJSObject = () =>  {
  return usePhysicalObjects.getState().getCurrentThreeJSObject()
}
window.debug.setTestTextureForCurrentObject = (testMaterial=material50cm) => {
  const objects =  usePhysicalObjects.getState()
  const selectedPhysicalObject = objects.getSelectedPhysicalObject(objects.selectedPhysicalObjectKey)
  if (selectedPhysicalObject && testMaterial) {
    const updatedObject = produce(selectedPhysicalObject, draft=> {
      if (draft.materialData==null) draft.materialData={files:null,materialProps:null};
      if (testMaterial?.files) draft.materialData.files = testMaterial.files;
      if (testMaterial?.materialProps) draft.materialData.materialProps = testMaterial.materialProps;
    })
    objects.updatePhysicalObject(updatedObject)
  }
  else if (!testMaterial) {
    const updatedObject = produce(selectedPhysicalObject, draft=> {
      draft.materialData={files:null,materialProps:null};
      draft.materialData.materialProps = deepClone(materialDefaults)
    })
    objects.updatePhysicalObject(updatedObject)
  }
}
window.debug.updateCurrentObject= (key,value) => {
  if (!key || !value) return null;
  const objects =  usePhysicalObjects.getState()
  const currentObject = objects.getSelectedPhysicalObject(objects.selectedPhysicalObjectKey)
  if (currentObject) {
    const newObject = produce(currentObject,draft => {
      draft[key] = value
    })
    objects.updatePhysicalObject(newObject)
  }
}
window.debug.updateMaterialPropsInCurrentObject = (key,value) => {
  if (!key || !value) return null;
  const objects =  usePhysicalObjects.getState()
  const currentObject = objects.getSelectedPhysicalObject(objects.selectedPhysicalObjectKey)
  if (currentObject) {
    const newObject = produce(currentObject,draft => {
      draft.materialData.materialProps[key] = value
    })
    objects.updatePhysicalObject(newObject)
  }
}

// Deferred: debugConcepts only registers window.debug.* helpers, but a static
// import chains firestore + the design_stream apiConfig into the entry chunk.
import('./design_stream_evt/debugConcepts').catch(() => {});
